import { Stack, SimpleGrid, Text, Box, Group, Badge, Progress, Skeleton, Alert } from '@mantine/core'
import { LineChart, BarChart } from '@mantine/charts'
import { IconCash, IconPercentage, IconPigMoney, IconChartBar, IconArrowRight, IconTrendingUp, IconTrendingDown } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { useFinopsData } from '@/hooks/useFinopsData'
import { SmallBox, WidgetCard, EmptyState } from '@/components/ui'
import { metrica } from '@/utils/metrica'

export function FinopsDashboard() {
  const {
    tenant, tenantId,
    summary, costByService, dailyCost,
  } = useFinopsData()

  if (!tenantId) return <Alert color="yellow">Selecione um tenant para visualizar o dashboard FinOps.</Alert>

  const s = summary.data
  const isLoading = summary.isLoading

  // Format data for charts
  const dailyChartData = (dailyCost.data?.daily ?? []).map(d => ({
    date: d.date.slice(5), // MM-DD
    'Custo USD': d.cost_usd,
  }))

  const serviceChartData = (costByService.data?.services ?? [])
    .sort((a, b) => b.cost_usd - a.cost_usd)
    .slice(0, 10)
    .map(svc => ({
      service: svc.service.replace('Amazon ', '').replace('AWS ', ''),
      'Custo USD': svc.cost_usd,
    }))

  const momPct = s?.mom_pct ?? 0
  const MomIcon = momPct > 0 ? IconTrendingUp : IconTrendingDown
  const momLabel = s?.period_label ?? 'MoM (mesmo período)'

  return (
    <Stack gap="xl">
      {/* KPI Row */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {isLoading ? (
          <>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </>
        ) : (
          <>
            <SmallBox
              label="Custo Mês Atual"
              value={s?.cost_available !== false ? `$${(s?.current_month_cost_usd ?? 0).toFixed(2)}` : 'N/A'}
              icon={<IconCash size={40} />}
              color="info"
            />
            <SmallBox
              label={`MoM — ${momLabel}`}
              value={s?.mom_pct != null ? `${s.mom_pct > 0 ? '+' : ''}${s.mom_pct.toFixed(1)}%` : (s?.period_label ?? 'N/A')}
              icon={<MomIcon size={40} />}
              color={momPct > 0 ? 'danger' : 'success'}
            />
            <SmallBox
              label="Budget Usado"
              value={s?.budget?.has_threshold ? `${(s.budget.pct_used ?? 0).toFixed(1)}%` : 'Sem threshold'}
              icon={<IconPercentage size={40} />}
              color={s?.budget?.exceeded ? 'danger' : s?.budget?.alert ? 'warning' : 'info'}
            />
            <SmallBox
              label="Economia Potencial"
              // As outras tres caixas deste arquivo TEM guarda (cost_available, has_threshold,
              // mom_pct != null). Escapou esta — e num produto de FinOps "nao ha economia a
              // capturar" e a conclusao que faz o cliente parar de usar a tela.
              value={metrica(s?.savings_potential_usd, (v) => `$${v.toFixed(2)}/mes`)}
              icon={<IconPigMoney size={40} />}
              color={s?.savings_potential_usd == null ? undefined : 'success'}
            />
          </>
        )}
      </SimpleGrid>

      {/* Charts */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <WidgetCard>
          <Text size="sm" fw={600} mb="md">Custo Diário</Text>
          {dailyCost.isLoading ? (
            <Skeleton height={250} />
          ) : dailyChartData.length > 0 ? (
            <LineChart
              h={250}
              data={dailyChartData}
              dataKey="date"
              series={[{ name: 'Custo USD', color: 'blue.6' }]}
              curveType="monotone"
              withDots={false}
              valueFormatter={(v) => `$${Number(v).toFixed(2)}`}
            />
          ) : (
            <EmptyState
              bare
              size="sm"
              accent="info"
              icon={IconChartBar}
              title="Sem dados de custo diário"
              description="A coleta de custos AWS ainda não retornou amostras para este tenant."
            />
          )}
        </WidgetCard>

        <WidgetCard>
          <Text size="sm" fw={600} mb="md">Top Serviços por Custo</Text>
          {costByService.isLoading ? (
            <Skeleton height={250} />
          ) : serviceChartData.length > 0 ? (
            <BarChart
              h={250}
              data={serviceChartData}
              dataKey="service"
              series={[{ name: 'Custo USD', color: 'teal.6' }]}
              orientation="vertical"
              valueFormatter={(v) => `$${Number(v).toFixed(2)}`}
            />
          ) : (
            <EmptyState
              bare
              size="sm"
              accent="info"
              icon={IconChartBar}
              title="Sem dados de custo por serviço"
              description="Aguardando agregação de custo por serviço AWS para este período."
            />
          )}
        </WidgetCard>
      </SimpleGrid>

      {/* Budget Progress */}
      {s?.budget?.has_threshold && (
        <WidgetCard>
          <Group justify="space-between" mb="sm">
            <Text size="sm" fw={600}>Budget</Text>
            <Badge
              color={s.budget.exceeded ? 'red' : s.budget.alert ? 'yellow' : 'teal'}
              variant="light"
              size="sm"
            >
              {s.budget.exceeded ? 'Excedido' : s.budget.alert ? 'Alerta' : 'Normal'}
            </Badge>
          </Group>
          <Progress
            value={Math.min(s.budget.pct_used ?? 0, 100)}
            color={s.budget.exceeded ? 'red' : s.budget.alert ? 'yellow' : 'teal'}
            size="lg"
            radius="sm"
            mb="xs"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              ${(s?.current_month_cost_usd ?? 0).toFixed(2)} de ${s.budget.threshold_usd.toFixed(2)}
            </Text>
            <Text size="xs" c="dimmed">{(s.budget.pct_used ?? 0).toFixed(1)}%</Text>
          </Group>
        </WidgetCard>
      )}

      {/* Quick link */}
      <Box>
        <Link to="/finops/recomendacoes" style={{ textDecoration: 'none' }}>
          <Group gap="xs" c="blue">
            <IconChartBar size={16} />
            <Text size="sm" fw={500}>Ver Recomendações de Economia</Text>
            <IconArrowRight size={14} />
          </Group>
        </Link>
      </Box>
    </Stack>
  )
}
